/**
 * GMDSS Simple Direct Guidance - No Mappings, No Complexity
 * Every requirement ID maps directly to its guidance
 */

export const GMDSS_SIMPLE_GUIDANCE = {
  // VHF Equipment
  'gmdss.vhf.radio': {
    id: 'gmdss.vhf.radio',
    name: 'VHF Radio with DSC',
    category: 'GMDSS',
    regulation: 'WBC3 Section 17.1',
    description: 'VHF marine radio with Digital Selective Calling - mandatory for all commercial vessels.',
    authority: 'WBC3 Section 17.1',
    placement: {
      requirements: [
        'Mandatory for all commercial vessels (WBC3 Section 17.1)',
        'VHF radio with Digital Selective Calling capability required',
        'Must be accessible to person in charge of navigation watch'
      ]
    },
    specifications: {
      type: 'VHF Marine Radio with DSC',
      standards: 'WBC3 Section 17.1 compliance required',
      mounting: 'Install at primary operating position'
    },
    maintenance: {
      mandatory: [
        'Regular operational testing required',
        'MMSI programming must be verified',
        'Channel 16 watch capability essential',
        'DSC functionality must be operational'
      ]
    }
  },

  'gmdss.vhf.portable.dsc': {
    id: 'gmdss.vhf.portable.dsc',
    name: 'Portable VHF DSC Radio',
    category: 'GMDSS',
    regulation: 'WBC3 Basic Equipment',
    description: 'Portable VHF DSC radio for all vessels - part of basic equipment requirements.',
    authority: 'WBC3 Table 17.2.1',
    placement: {
      requirements: [
        'Portable VHF DSC required for ALL vessels (WBC3 Table 17.2.1)',
        'Basic equipment - not area category dependent',
        'Must be readily accessible for emergency use'
      ]
    },
    specifications: {
      type: 'Portable VHF Radio with DSC',
      standards: 'WBC3 Table 17.2.1 compliance required',
      mounting: 'Portable equipment - multiple units recommended'
    },
    maintenance: {
      mandatory: [
        'Regular battery condition checks required',
        'DSC programming must be verified',
        'Emergency channel reception must be tested',
        'Waterproof integrity must be maintained'
      ]
    }
  },

  'gmdss.vhf.fixed.dsc': {
    id: 'gmdss.vhf.fixed.dsc',
    name: 'Fixed VHF DSC Radio Installation',
    category: 'GMDSS',
    regulation: 'WBC3 Section 17.1',
    description: 'Fixed VHF marine radio with Digital Selective Calling - mandatory for all commercial vessels.',
    authority: 'WBC3 Section 17.1',
    placement: {
      requirements: [
        'Mandatory for all commercial vessels (WBC3 Section 17.1)',
        'VHF radio with Digital Selective Calling capability required',
        'Must be accessible to person in charge of navigation watch'
      ]
    },
    specifications: {
      type: 'VHF Marine Radio with DSC',
      standards: 'WBC3 Section 17.1 compliance required',
      mounting: 'Install at primary operating position'
    },
    maintenance: {
      mandatory: [
        'Regular operational testing required',
        'MMSI programming must be verified',
        'Channel 16 watch capability essential',
        'DSC functionality must be operational'
      ]
    }
  },

  'gmdss.vhf.ch70.watch': {
    id: 'gmdss.vhf.ch70.watch',
    name: 'VHF Channel 70 DSC Watch',
    category: 'GMDSS',
    regulation: 'WBC3 Section 17.1',
    description: 'Digital Selective Calling controller for VHF distress and safety communications.',
    authority: 'WBC3 Section 17.1',
    placement: {
      requirements: [
        'Part of mandatory VHF Radio with DSC system',
        'Must be accessible to person in charge of navigation watch',
        'Distress alerting capability required'
      ]
    },
    specifications: {
      type: 'VHF DSC Controller',
      standards: 'WBC3 Section 17.1 compliance required',
      mounting: 'Integrated with VHF radio system'
    },
    maintenance: {
      mandatory: [
        'MMSI programming must be verified',
        'Distress alerting function must be operational',
        'Regular testing of DSC capabilities required',
        'Channel 70 monitoring required'
      ]
    }
  },

  // Emergency Equipment
  'gmdss.epirb.mandatory': {
    id: 'gmdss.epirb.mandatory',
    name: 'EPIRB (406MHz with GPS)',
    category: 'GMDSS',
    regulation: 'WBC3 Table 17.2.1',
    description: 'Emergency Position Indicating Radio Beacon - mandatory for Category 0-2 vessels.',
    authority: 'WBC3 Table 17.2.1',
    placement: {
      requirements: [
        'Mandatory for Category 0-2 vessels (WBC3 Table 17.2.1)',
        'Area Category 0-2 requirement (WBC3 Section 17.7)',
        'Must be accessible for emergency activation'
      ]
    },
    specifications: {
      type: 'EPIRB (406MHz with GPS)',
      standards: 'WBC3 Table 17.2.1 compliance required',
      mounting: 'Deck mounted with float-free capability'
    },
    maintenance: {
      mandatory: [
        'Regular inspection for damage required',
        'Battery expiry date must be monitored',
        'Float-free mechanism must be operational',
        'Registration with maritime authority required'
      ]
    }
  },

  'gmdss.plb.mandatory': {
    id: 'gmdss.plb.mandatory',
    name: 'Personal Locator Beacon',
    category: 'GMDSS',
    regulation: 'WBC3 Optional',
    description: 'Personal emergency beacon - optional equipment for enhanced crew safety.',
    authority: 'WBC3 Optional Equipment',
    placement: {
      requirements: [
        'Optional equipment for enhanced crew safety',
        'Personal emergency alerting capability',
        'Recommended for crew in high-risk operations'
      ]
    },
    specifications: {
      type: 'Personal Locator Beacon',
      standards: 'Maritime emergency beacon standards',
      mounting: 'Personal carry equipment'
    },
    maintenance: {
      recommended: [
        'Regular battery condition monitoring',
        'Crew training on activation procedures',
        'Waterproof integrity checks',
        'Storage in accessible location'
      ]
    }
  },

  'gmdss.sart': {
    id: 'gmdss.sart',
    name: 'Search and Rescue Transponder',
    category: 'GMDSS',
    regulation: 'WBC3 Optional',
    description: 'Radar transponder for location by rescue vessels - recommended with EPIRB.',
    authority: 'WBC3 Optional Equipment',
    placement: {
      requirements: [
        'Recommended with EPIRB installation',
        'Portable equipment for survival craft use',
        'Enhanced location capability for rescue operations'
      ]
    },
    specifications: {
      type: 'Radar Search and Rescue Transponder',
      standards: 'Maritime radar transponder standards',
      mounting: 'Portable equipment'
    },
    maintenance: {
      recommended: [
        'Regular battery condition monitoring',
        'Accessibility for emergency deployment',
        'Crew familiarization training required',
        'Waterproof integrity maintenance'
      ]
    }
  },

  'gmdss.sart.second': {
    id: 'gmdss.sart.second',
    name: 'Second Search and Rescue Transponder',
    category: 'GMDSS',
    regulation: 'WBC3 Optional',
    description: 'Additional radar transponder for enhanced rescue capability.',
    authority: 'WBC3 Optional Equipment',
    placement: {
      requirements: [
        'Additional SART for enhanced capability',
        'Portable equipment for survival craft use',
        'Enhanced location capability for rescue operations'
      ]
    },
    specifications: {
      type: 'Radar Search and Rescue Transponder',
      standards: 'Maritime radar transponder standards',
      mounting: 'Portable equipment'
    },
    maintenance: {
      recommended: [
        'Regular battery condition monitoring',
        'Accessibility for emergency deployment',
        'Crew familiarization training required',
        'Waterproof integrity maintenance'
      ]
    }
  },

  // Missing ID identified by audit - from RA verified guidance
  
  'gmdss.mf.dsc': {
    id: 'gmdss.mf.dsc',
    name: 'MF Radio Cats 0-4 A1 (UK)',
    equipment_type: 'mf_radio',
    
    placement: {
      requirements: [
        'Installed at radio operating position',
        'Antenna system suitable for MF transmission',
        'Connected to vessel power supply with backup',
        'DSC capability on appropriate MF frequencies'
      ]
    },
    
    specifications: {
      type: 'Medium Frequency radio installation with Digital Selective Calling',
      standards: 'MF radio installation with DSC per WBC3 Table 17.2.1 Note C',
      categories: 'Categories 0-4 operating in UK waters in Sea Area A1',
      uk_specific: 'Required in UK waters due to no MF listening watch by HM Coastguard',
      dsc_alerting: 'DSC alerting capability required for UK coastal waters',
      frequency: 'MF DSC frequencies per international allocation',
      reference: 'See MIN 698 for installation requirements'
    },
    
    maintenance: {
      mandatory: [
        'Annual radio safety inspection and testing',
        'DSC transmission capability verification',
        'Antenna system performance testing',
        'Emergency power supply verification'
      ],
      recommended: [
        'Monthly DSC test transmission on appropriate frequencies',
        'Check MF antenna tuning and SWR',
        'Verify emergency battery backup duration',
        'UK coastal DSC procedure training for crew'
      ]
    },
    
    authority: 'WBC3 Table 17.2.1 Note C | MIN 698'
  }
};

/**
 * Simple Direct Lookup - No mappings, no complexity
 * @param {string} itemId - Equipment requirement ID
 * @returns {Object|null} Guidance object or null
 */
export function getGMDSSSimpleGuidance(itemId) {
  return GMDSS_SIMPLE_GUIDANCE[itemId] || null;
}